/**
 * WHERE THE ROOM IS LOOKING, when it follows a lead: the steps from the whole
 * room down to this lead, each one a way back, and what this lead is looking
 * for. Shown above everything it narrows - the Dashboard, the views, the digs.
 */
import { useState } from 'react';
import type { BoardroomLead } from '../../../types';

export default function LeadBar({
  lead,
  busy,
  onGo,
  onEdit,
  onDirection,
  embedded = false,
}: {
  lead: BoardroomLead;
  busy: boolean;
  /** Follow this lead, or null for the whole room. */
  onGo: (leadId: number | null) => void;
  onEdit: (body: { title: string; text: string }) => Promise<void>;
  onDirection: () => void;
  /** Drawn as the top of the box it shares with the lens, without its own edge. */
  embedded?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(lead.title);
  const [text, setText] = useState(lead.text);
  const [saving, setSaving] = useState(false);
  const path = lead.path?.length ? lead.path : [{ id: lead.id, title: lead.title }];
  const above = path.length > 1 ? path[path.length - 2].id : null;
  const crumb =
    'rounded px-1 text-indigo-700 hover:bg-indigo-100 hover:underline disabled:opacity-50 dark:text-indigo-300 dark:hover:bg-indigo-900/40';
  const btn =
    'shrink-0 rounded border border-indigo-300 px-1.5 py-0.5 text-xs text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-200 dark:hover:bg-indigo-900/40';

  const save = async () => {
    setSaving(true);
    try {
      await onEdit({ title: title.trim(), text: text.trim() });
      setEditing(false);
    } catch {
      /* the page shows the error; the form stays open */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`shrink-0 bg-indigo-50 px-3 py-2 text-sm dark:bg-indigo-900/20 ${
        embedded ? '' : 'rounded-lg border border-indigo-200 dark:border-indigo-800'
      }`}
      data-testid="lead-bar"
    >
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
        <span className="mr-1 text-[11px] uppercase tracking-wide text-indigo-500 dark:text-indigo-300">Following lead:</span>
        <button type="button" className={crumb} disabled={busy} onClick={() => onGo(null)} data-testid="lead-crumb-room">
          Whole room
        </button>
        {path.map((p, i) => (
          <span key={p.id} className="inline-flex items-center gap-1">
            <span className="text-indigo-300 dark:text-indigo-600">›</span>
            {i === path.length - 1 ? (
              <span className="px-1 font-medium text-indigo-950 dark:text-indigo-100">{p.title}</span>
            ) : (
              <button type="button" className={crumb} disabled={busy} onClick={() => onGo(p.id)} data-testid={`lead-crumb-${p.id}`}>
                {p.title}
              </button>
            )}
          </span>
        ))}
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {!editing && (
            <button
              type="button"
              className={btn}
              onClick={() => {
                setTitle(lead.title);
                setText(lead.text);
                setEditing(true);
              }}
            >
              Edit
            </button>
          )}
          <button
            type="button"
            className={btn}
            disabled={busy}
            onClick={onDirection}
            title="Make what this lead is looking for the room's own focus, and stop following it as a lead"
            data-testid="lead-direction"
          >
            Make this the room's direction
          </button>
          <button type="button" className={btn} disabled={busy} onClick={() => onGo(above)} data-testid="lead-back">
            ‹ Back to {above ? path[path.length - 2].title : 'the whole room'}
          </button>
        </span>
      </div>
      {editing ? (
        <div className="mt-2 space-y-1.5">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            aria-label="Lead name"
            maxLength={120}
            className="w-full rounded border border-indigo-300 px-2 py-1 text-sm dark:border-indigo-700 dark:bg-gray-800 dark:text-gray-100"
          />
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            aria-label="What you are looking for"
            rows={2}
            maxLength={1000}
            className="w-full rounded border border-indigo-300 px-2 py-1 text-sm dark:border-indigo-700 dark:bg-gray-800 dark:text-gray-100"
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !title.trim() || !text.trim()}
              className="rounded bg-indigo-600 px-2 py-0.5 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-0.5 text-xs text-indigo-900/80 dark:text-indigo-200/80" data-testid="lead-text">
          {lead.text}
        </p>
      )}
    </div>
  );
}
